import React from 'react';
import { useTranslation } from 'react-i18next';
import ModalV2 from 'common/components/Modal/ModalV2';
import { LoadingButton } from 'common/components/Form/Button';
import { SecondaryButton } from 'common/components/Form/SecondaryButton';
import { getDataExportErrorMessages } from 'common/queries/dataExport';
import { CreateDataExportWebhookPayload } from 'common/types/dataExport';
import {
  describedBy,
  validateWebhookForm,
  WEBHOOK_SECRET_MIN_LENGTH,
  WebhookFormErrors,
} from 'modules/settings/components/DataExport/utils';
import {
  ErrorBanner,
  Field,
  FieldError,
  FieldHint,
  FieldLabel,
  ModalActions,
  ModalBody,
  TextField,
} from 'modules/settings/components/DataExport/styled';

interface Props {
  isSubmitting: boolean;
  /** Resolves when the webhook was registered (the parent then closes this modal), rejects with the request error. */
  onSubmit: (payload: CreateDataExportWebhookPayload) => Promise<unknown>;
  onClose: () => void;
}

/** Mounted only while open, so the form starts empty every time. Inputs survive a rejected submit. */
const AddWebhookModal: React.FC<Props> = ({ isSubmitting, onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [url, setUrl] = React.useState('');
  const [secret, setSecret] = React.useState('');
  const [fieldErrors, setFieldErrors] = React.useState<WebhookFormErrors>({});
  const [serverErrors, setServerErrors] = React.useState<string[]>([]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = validateWebhookForm({ url, secret });
    setFieldErrors(errors);
    setServerErrors([]);
    if (errors.url || errors.secret) return;

    onSubmit({ url: url.trim(), ...(secret ? { secret } : {}) }).catch(error => {
      const messages = getDataExportErrorMessages(error);
      setServerErrors(messages.length ? messages : [t('dataExport.webhooks.addFailed')]);
    });
  };

  return (
    <ModalV2
      isOpen
      appearance='card'
      modalId='data-export-add-webhook-modal'
      title={t('dataExport.webhooks.form.title')}
      clickOutsideClose={!isSubmitting}
      onClose={onClose}
      data-testid='add-webhook-modal'
    >
      <form onSubmit={handleSubmit} noValidate>
        <ModalBody>
          <Field>
            <FieldLabel htmlFor='data-export-webhook-url'>{t('dataExport.webhooks.form.urlLabel')}</FieldLabel>
            <TextField
              id='data-export-webhook-url'
              name='url'
              type='text'
              placeholder={t('dataExport.webhooks.form.urlPlaceholder')}
              value={url}
              onChange={e => setUrl(e.target.value)}
              aria-invalid={!!fieldErrors.url}
              aria-describedby={describedBy('data-export-webhook-url-hint', fieldErrors.url && 'data-export-webhook-url-error')}
              autoFocus
              required
            />
            <FieldHint id='data-export-webhook-url-hint'>{t('dataExport.webhooks.form.urlHint')}</FieldHint>
            {fieldErrors.url && (
              <FieldError id='data-export-webhook-url-error'>{t(`dataExport.webhooks.form.${fieldErrors.url}`)}</FieldError>
            )}
          </Field>
          <Field>
            <FieldLabel htmlFor='data-export-webhook-secret'>{t('dataExport.webhooks.form.secretLabel')}</FieldLabel>
            <TextField
              id='data-export-webhook-secret'
              name='secret'
              type='text'
              mono
              value={secret}
              onChange={e => setSecret(e.target.value)}
              aria-invalid={!!fieldErrors.secret}
              aria-describedby={describedBy('data-export-webhook-secret-hint', fieldErrors.secret && 'data-export-webhook-secret-error')}
            />
            <FieldHint id='data-export-webhook-secret-hint'>
              {t('dataExport.webhooks.form.secretHint', { count: WEBHOOK_SECRET_MIN_LENGTH })}
            </FieldHint>
            {fieldErrors.secret && (
              <FieldError id='data-export-webhook-secret-error'>
                {t('dataExport.webhooks.form.secretTooShort', { count: WEBHOOK_SECRET_MIN_LENGTH })}
              </FieldError>
            )}
          </Field>
          {serverErrors.length > 0 && (
            <ErrorBanner role='alert'>
              {serverErrors.map(message => (
                <p key={message}>{message}</p>
              ))}
            </ErrorBanner>
          )}
          <ModalActions>
            <SecondaryButton type='button' color='white' isButtonV2 disabled={isSubmitting} onClick={onClose}>
              {t('common.cancel')}
            </SecondaryButton>
            <LoadingButton type='submit' color='lightPurple' pill={false} isButtonV2 loading={isSubmitting} disabled={isSubmitting}>
              {t('dataExport.webhooks.form.submit')}
            </LoadingButton>
          </ModalActions>
        </ModalBody>
      </form>
    </ModalV2>
  );
};

export default AddWebhookModal;
